import React, {
  ReactNode,
  useCallback,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { usePopper } from 'react-popper';
import QuestionIcon from 'app/components/Icons/QuestionIcon';
import './style.scss';

interface SelectButtonHintProps {
  hint: string | ReactNode;
  position?: 'right' | 'left' | 'top' | 'bottom';
}

const SelectButtonHint: React.FC<SelectButtonHintProps> = ({
  hint,
  position = 'bottom',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const { styles, attributes, update } = usePopper(
    buttonRef.current,
    popupRef.current,
    {
      placement: position,
      modifiers: [
        { name: 'flip', enabled: true },
        { name: 'preventOverflow', enabled: true },
        { name: 'offset', options: { offset: [0, 8] } },
        {
          name: 'arrow',
          enabled: true,
          options: { element: arrowRef.current, padding: 8 },
        },
      ],
    },
  );

  const handleToggle = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      event.stopPropagation();
      setIsOpen((value) => !value);
    },
    [],
  );

  useLayoutEffect(() => {
    if (isOpen && popupRef.current) {
      setHasMounted(true);
      if (update) update();
    } else {
      setHasMounted(false);
    }
  }, [isOpen, update]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return undefined;
    const onClick = (e: MouseEvent) => {
      if (
        !buttonRef.current?.contains(e.target as Node) &&
        !popupRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isOpen]);

  return (
    <>
      <div
        className={clsx('select_button_hint', {
          position_left: position === 'left',
        })}
      >
        <div className="select_button_hint__block">
          <button
            className="select_button_hint__block__icon"
            ref={buttonRef}
            onClick={handleToggle}
            type="button"
            tabIndex={0}
          >
            <QuestionIcon />
          </button>
        </div>
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            id="popper"
            className={clsx('select_button_hint__block__popup', {
              open: isOpen,
            })}
            ref={popupRef}
            style={hasMounted ? styles.popper : undefined}
            {...(hasMounted ? attributes.popper : {})}
          >
            <div
              ref={arrowRef}
              className="select_button_hint__block__arrow"
              style={hasMounted ? styles.arrow : undefined}
              data-popper-arrow
            />
            <div className="select_button_hint__block__text">{hint}</div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SelectButtonHint;
